export const NEAR_ZERO: number = 0.0000001;
export function closeNumbers(a:number, b:number): boolean {
    return Math.abs(a - b) < NEAR_ZERO;
}
export function closePoints(p0:number[], p1:number[]): boolean {
    return closeNumbers(p0[0],p1[0]) && closeNumbers(p0[1],p1[1]);
}
export function avoidZero(a:number): number {
  return closeNumbers(a,0) ? NEAR_ZERO * (a < 0 ? -1 : 1) : a;
}
export function between(a:number, i0:number, i1:number, inclusive: boolean): boolean {
    return inclusive===true ? 
        a > (i0-NEAR_ZERO) && a < (i1+NEAR_ZERO)
    :
        a > (i0+NEAR_ZERO) && a < (i1-NEAR_ZERO);
}
export function lessThen(a: number, b: number): boolean{
    return a < b-NEAR_ZERO;
}
export function greaterThen(a: number, b: number): boolean{
    return a > b+NEAR_ZERO;
}

/**
         * A straight line defined between two end points
         */
        export class Line {
            public c: number[][]; //the coordinates of the end points making up this line
            public dT: number; //the time it takes to travel the line from start to end.
            public xMax: number; //the index number of the end point that has the maximum x coordinate
            private slope: number; //the slope of the line
            private offset: number; // the offset from the y-axis when x = 0
            private isVertical: boolean; //flag indicating whether the line is strict vertical (x0 - x1 < NEAR_ZERO)
            private isHorizontal: boolean; //flag indicating whether the line is strict horizontal (y0 - y1< NEAR_ZERO)
            private alpha: number[]; // angle with positive x-axis in radians and the angle perpendicular to it
//            public intersections: number[][]; //intersection points with other lines;
            


            /**
             * Constructs a line defined from point (x0,y0) towards point (x1,y1)
             * @param x0 the x-coordinate of starting point
             * @param y0 the y-coordinate of starting point
             * @param x1 the x-coordinate of end point
             * @param y1 the y-coordinate of end point
             * @param dT the time it takes to travel the line from start to end.
             */
            constructor(x0: number, y0: number, x1: number, y1: number, dT: number, public id?: string) {
                this.c = [
                    [x0, y0],
                    [x1, y1]
                ];
                this.dT = dT;
                this.xMax = null;

                this.slope = null;
                this.offset = null;

                this.isVertical = null;
                this.isHorizontal = null;
                this.alpha = null;
//                this.intersections = null; 
                this.init();
            }
            /**
             * Returns the value of the maximum x value
             */
            public getXMax(): number {
                return this.c[this.xMax][0];
            }
            /**
             * Calculates some mathematical properties of this line.
             */
            private init(): Line {
                this.xMax = this.c[0][0] >= this.c[1][0] ? 0 : 1;

                this.slope = (this.c[0][1] - this.c[1][1]) /
                    (this.c[0][0] - this.c[1][0]);
                this.offset = this.c[0][1] - this.c[0][0] * this.slope;

                this.isVertical = closeNumbers(this.c[0][0],this.c[1][0]);
                this.isHorizontal = closeNumbers(this.c[0][1],this.c[1][1]);

                return this;
            }

            /**
             * returns 0 if given coordinate c equals starting point and 1 if c equals end point. -1 otherwise
             * @param c coordinate to be checked
             */
            public getIndex(c: number[]): number {
                
                return closePoints(this.c[0],c) ? 0 : closePoints(this.c[1],c)   ? 1 : -1;
            }

            private initAlpha(): void {
                if (this.alpha === null) {
                    let x: number = this.c[1][0] - this.c[0][0];
                    let y: number = this.c[1][1] - this.c[0][1];
                    let l: number = Math.sqrt(x * x + y * y);
                    let cosAlpha: number = x / l;
                    let alpha0: number = Math.acos(x / l) * (y < 0 ? -1 : 1) + (y < 0 ? 2 * Math.PI : 0);
                    let alpha1: number = alpha0 + (alpha0 >= Math.PI ? -Math.PI : Math.PI);
                    this.alpha = [alpha0, alpha1];
                }
            }

            /**
             * Returns angle this line makes with a line that arrives at coordinate c with given angle. 
             * When inverse is set to true, the 180 degrees opposite is returned.
             * @param c one of the end points
             * @param angleOffset the offset to apply to angle. Defaults to 0
             * @param inverse optional flag indicating we want the inverse angle. Defaults to false
             */
            public getAngle(c: number[], angleOffset = 0, inverse = false): number {
                let i: number = this.getIndex(c);
                if (i < 0) return undefined;
                this.initAlpha();
                let alpha: number = this.alpha[inverse === true ? 1 - i : i] - (angleOffset % (2 * Math.PI));
                return alpha < 0 ? alpha + 2 * Math.PI : alpha;
            }

            /**
             * Returns inverse angle this line makes with a line that arrives at coordinate c with given angle. 
             * @param c one of the end points
             * @param angleOffset the offset to apply to angle(optional).
             */
            public getInverseAngle(c: number[], angleOffset ? : number): number {
                return this.getAngle(c, angleOffset, true);
            }

            /*
            public addIntersection(intersection: number[]): void{
                if(!this.intersections){
                    this.intersections = [intersection];
                }else{
                    if( this.intersections.findIndex( inter => closePoints(inter,intersection)) ==-1 ){
                            this.intersections.push(intersection);
                            this.intersections.sort((p0,p1) => { 
                              let xL0: number = p0[0] - this.c[0][0];
                    let yL0: number = p0[1] - this.c[0][1];
                    let l0: number = Math.sqrt(xL0 * xL0 + yL0 * yL0);
                    let xL1: number = p1[0] - this.c[0][0];
                    let yL1: number = p1[1] - this.c[0][1];
                    let l1: number = Math.sqrt(xL1 * xL1 + yL1 * yL1);
                    return l0-l1;
                    });
                }
                
            }
        }
        */
            public getSplits(intersections: number[][]): Line[] {
                let succesorSegments: Line[] = [];
                if(intersections && intersections.length>0){ 
                    let intersection: number[];
                    let parts: Line[];
                    for(let i=0; i<intersections.length; i++){
                        intersection = intersections[i];
                        parts = parts ? parts[1].split(intersection): this.split(intersection);
                        succesorSegments.push(parts[0]);
                        if(i==intersections.length-1){
                          succesorSegments.push(parts[1]);
                        }
                    }
                }else{
                    succesorSegments.push(new Line(this.c[0][0], this.c[0][1], this.c[1][0], this.c[1][1], this.dT, this.id));
                }
                return succesorSegments;
            }

            /**
             * splits this line and returns a line that is defined from point (x0,y0) towards point (x,y)
             * and a line that is defined from point (x,y) towards point (x1,y1).
             * @param point the coordinate to make the split
             */
            public split(point: number[]): Line[] {
                let x: number = point[0];
                let y: number = point[1];
                //first figure out the new timing parameters
                let xL0: number = x - this.c[0][0];
                let yL0: number = y - this.c[0][1];
                let l0: number = Math.sqrt(xL0 * xL0 + yL0 * yL0);

                let xL1: number = this.c[1][0] - x;
                let yL1: number = this.c[1][1] - y;
                let l1: number = Math.sqrt(xL1 * xL1 + yL1 * yL1);
                    
                let dT1 = this.dT * l1 / (l0 + l1);
                let segment1 = new Line(x, y, this.c[1][0], this.c[1][1], dT1, this.id+'*1');
                let dT0 = this.dT * l0 / (l0 + l1)    
                let segment0 = new Line(this.c[0][0], this.c[0][1], x, y, dT0, this.id+'*0');
                return [ segment0, segment1 ];
            }

            /**
             * Test whether end points of given line equals the end points of this line. If the start point matches p0 and 
             * the end point matches p1 then 1 gets returned. If start point matches p1 and end point matches p0 and 
             * direction is false then -1 gets returned. Otherwise 0 gets returned.
             * @param other the Line to check against
             * @param direction optional parameter that signals if the direction should also be equal. Defaults to false
             */
            public equals(other: Line, direction = false): number {
                if(other==null) return 0;
                return this.hasEndPoints(other.c[0],other.c[1],direction);    
            }
            /**
             * Test whether given end points equals the end points of this line. If the start point matches p0 and 
             * the end point matches p1 then 1 gets returned. If start point matches p1 and end point matches p0 and 
             * direction is false then -1 gets returned. Otherwise 0 gets returned.
             * @param p0 the first end point to check against
             * @param p1 the first end point to check against
             * @param direction optional parameter that signals if the direction should also be equal. Defaults to false
             */
            public hasEndPoints(p0:number[], p1:number[], direction = false): number {
                let result = 0;    
                if(p0!=null && p1!=null){
                    result = 
                    closePoints(this.c[0],p0) &&
                    closePoints(this.c[1],p1) ? 1 :
                    closePoints(this.c[0],p1) &&
                    closePoints(this.c[1],p0) ? -1 : 0 
                }
                return result;    
            }
            /**
             * Returns the intersection coordinate between this and the other Line. Returns undefined when
             * the segments do not intersect. When inclusive is set to true, the end points are included in the 
             * intersection test
             * @param other the Line to check against
             * @param inclusive optional parameter that signals if the end point are valid intersection points. Defaults to false
             */
            public intersection(other: Line, inclusive = false): number[] {
                if ((other.c[0][0] < Math.min(this.c[0][0], this.c[1][0]) && other.c[1][0] < Math
                        .min(this.c[0][0], this.c[1][0])) ||
                    (other.c[0][0] > Math.max(this.c[0][0], this.c[1][0]) && other.c[1][0] > Math
                        .max(this.c[0][0], this.c[1][0])) ||
                    (other.c[0][1] < Math.min(this.c[0][1], this.c[1][1]) && other.c[1][1] < Math
                        .min(this.c[0][1], this.c[1][1])) ||
                    (other.c[0][1] > Math.max(this.c[0][1], this.c[1][1]) && other.c[1][1] > Math
                        .max(this.c[0][1], this.c[1][1])) ||
                    (!isFinite(this.slope) && !isFinite(other.slope)) ||
                    closeNumbers(other.slope,this.slope)) {
                    return undefined;
                } else {
                    let x;
                    if (!isFinite(this.offset)) {
                        x = this.c[0][0];
                        if ((inclusive === true &&
                                x >= Math.min(other.c[0][0], other.c[1][0]) && x <= Math
                                .max(other.c[0][0], other.c[1][0])) ||
                            (x > Math.min(other.c[0][0], other.c[1][0]) && x < Math
                                .max(other.c[0][0], other.c[1][0]))) {
                            return [x, other._y(x)];
                        }
                    } else if (!isFinite(other.offset)) {
                        x = other.c[0][0];
                        if ((inclusive === true &&
                                x >= Math.min(this.c[0][0], this.c[1][0]) && x <= Math
                                .max(this.c[0][0], this.c[1][0])) ||
                            (x > Math.min(this.c[0][0], this.c[1][0]) && x < Math
                                .max(this.c[0][0], this.c[1][0]))) {
                            return [x, this._y(x)];
                        }
                    } else {
                        let x0a: number = this.c[0][0];
                        let y0a: number = this.c[0][1];
                        let x0b: number = other.c[0][0];
                        let y0b: number = other.c[0][1];
                        let dXa: number = this.c[1][0] - x0a;
                        let dYa: number = this.c[1][1] - y0a;
                        let dXb: number = other.c[1][0] - x0b;
                        let dYb: number = other.c[1][1] - y0b;

                        let tb: number = (dXa * (y0a - y0b) + dYa * (x0b - x0a)) / (dXa * dYb - dXb * dYa);
                        let ta: number =  (x0b + tb*dXb - x0a) / dXa; //for completeness

                        if (inclusive === true) {

                            if (between(ta,0,1,true) &&
                                between(tb,0,1,true)) {
                                return other.get(tb * other.dT);
                            }
                        } else {
                            if (between(ta,0,1,false) &&
                                between(tb,0,1,false)) {
                                return other.get(tb * other.dT);
                            }
                        }
                    }
                    return undefined;
                }
            }
            /**
             * Returns the position on the line after 'travelling' a given amount of time where p = p0 for t=0 and p = p1 for t = dT.
             * @param _t the moment where to get the position for 
             */
            public get(_t: number): number[] {
                var t = _t / this.dT;
                let x: number = this.c[1][0] - this.c[0][0];
                let y: number = this.c[1][1] - this.c[0][1];

                return [
                    this.c[0][0] + t * x,
                    this.c[0][1] + t * y
                ]
            }
            /**
             * Returns the y for given x. If x is not in the scope of the
             * Line the value for y is undefined. When exclusive is
             * true then the end points are not considered to be part of the
             * Line.
             * @param x the x coordinate
             * @param exclusive not the end points. Defaults to false
             */
            private y(x: number, exclusive = false): number {
                return (exclusive === true && (x <= Math
                        .min(this.c[0][0], this.c[1][0]) || x >= Math.max(this.c[0][0],
                            this.c[1][0]))) ||
                    (x < Math.min(this.c[0][0], this.c[1][0]) || x > Math.max(
                        this.c[0][0], this.c[1][0])) ? undefined : this._y(x);
            }
            /**
             * Returns the x for given y. If y is not in the scope of the
             * Line the value for x is undefined. When exclusive is
             * true then the end points are not considered to be part of the
             * Line.
             * @param y the y coordinate
             * @param exclusive not the end points. Defaults to false
             */
            private x(y: number, exclusive = false): number {
                return (exclusive === true && (y <= Math
                        .min(this.c[0][1], this.c[1][1]) || y >= Math.max(this.c[0][1],
                            this.c[1][1]))) ||
                    (y < Math.min(this.c[0][1], this.c[1][1]) || y > Math.max(
                        this.c[0][1], this.c[1][1])) ? undefined : this._x(y);
            }

            /**
             * Returns the y for given x for a continuous line through the end points.
             * @param x the x coordinate
             */
            private _y(x: number): number {
                if (this.isVertical) { //segment is vertical
                    if (closeNumbers(x, this.c[0][0])) { //x is very close or on to segment
                        return this.c[0][1];
                    } else {
                        return undefined;
                    }
                } else if (!closeNumbers(this.slope, 0)) { //segment is NOT horizontal
                    return x * this.slope + this.offset;
                } else { // segment is horizontal
                    return this.c[0][1];
                }
            }

            /**
             * Returns the x for given y for a continuous line through the end points.
             * @param y the y coordinate
             */
            private _x(y: number): number {
                if (this.isVertical) { //segment is vertical
                    return this.c[0][0];
                } else if (!closeNumbers(this.slope, 0)) { //segment is NOT horizontal
                    return (y - this.offset) / this.slope;
                } else { // segment is horizontal
                    if (closeNumbers(y, this.c[0][1])) { //y is very close or on to segment
                        return this.c[0][0];
                    } else {
                        return undefined;
                    }
                }
            }

            /**
             * Returns string representation.
             */
            public toString(): string {
                return `Line( c:[[${this.c[0][0]}, ${this.c[0][1]}],[${this.c[1][0]}, ${this.c[1][1]}]], dT: ${this.dT} )`;
            }
        }
